import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StudentProgress } from '../types';
import { generateCertificate } from '../utils/certificate';

interface CertificateProps {
  progress: StudentProgress;
  onReset: () => void;
}

const Certificate: React.FC<CertificateProps> = ({ progress, onReset }) => {
  const navigate = useNavigate();
  const [googleApiStatus, setGoogleApiStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [googleApiError, setGoogleApiError] = useState<string>('');

  const submitToGoogleScript = async () => {
    console.log('🌍 [GOOGLE SCRIPT DEBUG] Environment:', process.env.NODE_ENV === 'production' ? 'Production' : 'Development');

    // Check if Google Apps Script URL is configured
    const googleScriptUrl = process.env.REACT_APP_GOOGLE_SCRIPT_URL;

    if (!googleScriptUrl) {
      console.log('🔧 [GOOGLE SCRIPT DEBUG] Attempting google script publishing: REACT_APP_GOOGLE_SCRIPT_URL not configured');
      console.log('Google Apps Script URL not configured, skipping submission');
      setGoogleApiStatus('success'); // Consider it successful since it's optional
      return;
    }

    console.log('🔧 [GOOGLE SCRIPT DEBUG] Attempting google script publishing: REACT_APP_GOOGLE_SCRIPT_URL configured');
    setGoogleApiStatus('pending');
    setGoogleApiError('');

    try {
      const response = await fetch(googleScriptUrl, {
        mode: 'no-cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: progress.email,
          firstName: progress.firstName,
          lastName: progress.lastName,
          studentId: progress.studentId,
          submissionId: progress.testId
        })
      });

      // With no-cors mode, we can't read the response or check status
      // We'll assume success if the request doesn't throw an error
      console.log('✅ [GOOGLE SCRIPT DEBUG] Success');
      console.log('Google Apps Script request sent successfully');
      setGoogleApiStatus('success');
    } catch (error) {
      console.error('❌ [GOOGLE SCRIPT DEBUG] Failed');
      console.error(`❌ [GOOGLE SCRIPT DEBUG] Error message is: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
      console.error('Error submitting to Google Apps Script:', error);
      setGoogleApiError(error instanceof Error ? error.message : 'Unknown error occurred');
      setGoogleApiStatus('error');
    }
  };

  // Auto-submit to Google Apps Script when component mounts
  useEffect(() => {
    submitToGoogleScript();
  }, []);

  const handleDownloadCertificate = () => {
    generateCertificate(progress);
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Congratulations!
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            You have successfully completed the Safety Quiz with a perfect score!
          </p>
          <p className="text-lg text-gray-500">
            Completed on {formatDate(progress.completedAt!)}
          </p>
        </div>

        {/* Certificate Preview */}
        <div className="bg-white rounded-xl shadow-2xl border-4 border-green-200 p-8 mb-8">
          <div className="text-center border-2 border-gray-300 p-8 bg-gradient-to-br from-blue-50 to-green-50">
            <h2 className="text-3xl font-serif font-bold text-gray-800 mb-6">
              CERTIFICATE OF COMPLETION
            </h2>

            <div className="border-t-2 border-b-2 border-gray-400 py-6 my-6">
              <p className="text-lg text-gray-600 mb-2">This certifies that</p>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {progress.firstName} {progress.lastName}
              </h3>
              <p className="text-lg text-gray-600 mb-2">Student ID: {progress.studentId}</p>
              <p className="text-lg text-gray-600">
                has successfully completed the
              </p>
              <h4 className="text-xl font-bold text-blue-800 my-3">
                Workshop Safety Training Quiz
              </h4>
              <p className="text-lg text-gray-600">
                with a perfect score of 100%
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
              <div>
                <p className="font-semibold">Completion Date:</p>
                <p>{formatDate(progress.completedAt!)}</p>
              </div>
              <div>
                <p className="font-semibold">Test ID:</p>
                <p className="font-mono text-xs">{progress.testId}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Google Apps Script Status */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Submission Status</h3>
          <div className="flex items-center space-x-3">
            {googleApiStatus === 'pending' && (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="text-blue-600">Submitting results to system...</span>
              </>
            )}
            {googleApiStatus === 'success' && (
              <>
                <div className="flex-shrink-0 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-green-600">Successfully submitted to system!</span>
              </>
            )}
            {googleApiStatus === 'error' && (
              <>
                <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <span className="text-red-600">Submission failed: {googleApiError}</span>
                  <button
                    onClick={submitToGoogleScript}
                    className="ml-3 text-sm bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded-md transition-colors duration-200"
                  >
                    Retry
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleDownloadCertificate}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Download Certificate</span>
          </button>

          <button
            onClick={() => navigate('/results')}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200"
          >
            Back to Results
          </button>

          <button
            onClick={onReset}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200"
          >
            Start New Quiz
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>This certificate serves as proof of successful completion of the Workshop Safety Training.</p>
          <p className="mt-2">Please keep this certificate for your records.</p>
        </div>
      </div>
    </div>
  );
};

export default Certificate;